import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = window.location.origin;

// Generate or retrieve session ID for anonymous tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sd_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sd_session_id', sessionId);
  }
  return sessionId;
};

// Map route paths to page titles
const PAGE_TITLES = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/soul-journal': 'Soul Journal',
  '/journal': 'Soul Journal',
  '/communities': 'Sister Circles',
  '/circles': 'Accountability Circles',
  '/messages': 'Messages',
  '/coaching': 'Coaching',
  '/coaching-hub': 'Coaching Hub',
  '/sessions': 'Sessions',
  '/settings': 'Settings',
  '/retreat': 'Costa Rica Retreat',
  '/legacy-vision': 'YUMMY Declaration',
  '/growth-tools': 'Growth Tools',
  '/she': 'SHE AI',
  '/library': 'The Vault',
  '/support': 'Support',
  '/activation-pathway': 'Activation Pathway',
  '/accountability-circle': 'Accountability Circle',
  '/truth-reset': 'Truth Reset',
  '/challenges': '6-Week Challenges',
  '/admin': 'Admin Dashboard',
  '/diagnostic': 'Inner Edge Diagnostic',
  '/profile': 'My Profile',
  '/pathways': 'Pathways',
  '/inner-circle': 'Inner Circle',
  '/inner-edge': 'Inner Edge',
  '/login': 'Login',
  '/register': 'Register',
  '/gift-membership': 'Gift Membership',
};

/**
 * Hook to track page views and user engagement
 * Tracks ALL visitors (anonymous + authenticated)
 */
export const usePageTracking = () => {
  const location = useLocation();
  const lastTrackedPath = useRef(null);
  
  const trackPageView = useCallback(async (path, title) => {
    try {
      const sessionId = getSessionId();
      const referrer = document.referrer || '';
      const userAgent = navigator.userAgent;
      
      // Track anonymously (no auth required)
      await axios.post(`${API_URL}/api/analytics/track-page-view`, null, {
        params: {
          page: path,
          referrer: referrer,
          user_agent: userAgent,
          session_id: sessionId
        }
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience for tracking
      console.debug('Page tracking failed:', error.message);
    }
  }, []);
  
  useEffect(() => {
    const path = location.pathname;
    
    // Don't track the same page twice in a row
    if (path === lastTrackedPath.current) return;
    
    // Get page title from map or generate from path
    let title = PAGE_TITLES[path];
    if (!title) {
      // Check for dynamic routes
      if (path.startsWith('/circles/')) title = 'Circle Details';
      else if (path.startsWith('/communities/')) title = 'Community';
      else if (path.startsWith('/coaching/')) title = 'Coaching Session';
      else if (path.startsWith('/admin/')) title = 'Admin';
      else {
        // Generate title from path
        title = path.split('/').pop()?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase()) || 'Page';
      }
    }
    
    // Track the page view
    trackPageView(path, title);
    lastTrackedPath.current = path;
    
  }, [location.pathname, trackPageView]);
};

/**
 * Hook to track login events
 */
export const useLoginTracking = () => {
  const trackLogin = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await axios.post(
        `${API_URL}/api/tracking/login`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.debug('Login tracking failed:', error.message);
    }
  }, []);
  
  return { trackLogin };
};

/**
 * Hook to track logout events
 */
export const useLogoutTracking = () => {
  const trackLogout = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      await axios.post(
        `${API_URL}/api/tracking/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.debug('Logout tracking failed:', error.message);
    }
  }, []);
  
  return { trackLogout };
};

export default usePageTracking;
